import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { DollarSign } from "lucide-react";
import { useMemo } from "react";

import {
  locationSalesGetSalesByLocation,
  locationSalesGetSalesBySalesCategory,
  locationSalesGetSalesTotals,
  type SalesByLocationEntry,
  type SalesBySalesCategoryEntry,
} from "~/api-refs";
import { toConvexTableId } from "~/lib/convex-table-id";

import { SalesDataTable } from "../sales-data-table";
import { StatCard } from "../stat-card";

interface SalesSummaryViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const SalesSummaryView = ({
  locationId,
  dateRange,
  tableId,
}: SalesSummaryViewProps) => {
  return (
    <div className="space-y-8">
      <SalesTotals dateRange={dateRange} locationId={locationId} />
      <SalesByLocation
        dateRange={dateRange}
        locationId={locationId}
        tableId={`${tableId}-location`}
      />
      <SalesBySalesCategory
        dateRange={dateRange}
        locationId={locationId}
        tableId={`${tableId}-category`}
      />
    </div>
  );
};

// Sales Totals Component
const SalesTotals = ({
  dateRange,
  locationId,
}: {
  dateRange: { from: Date; to: Date };
  locationId: string;
}) => {
  const { data } = useQuery(
    convexQuery(locationSalesGetSalesTotals, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );

  return (
    <section className="grid grid-cols-3 gap-5">
      {[
        {
          title: "Gross Sales Total",
          value:
            data?.grossSales?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }) ?? "Loading...",
        },
        {
          title: "Net Sales Total",
          value:
            data?.netSales?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }) ?? "Loading...",
        },
        {
          title: "Total Orders",
          value: data?.ordersCount?.toLocaleString("en-US") ?? "Loading...",
        },
      ].map(({ title, value }) => {
        const id = title.replace(/\s+/g, "-").toLowerCase();
        return (
          <StatCard
            icon={DollarSign}
            id={id}
            key={title}
            title={title}
            value={value}
          />
        );
      })}
    </section>
  );
};

// Sales by Location Component
type SalesByLocationData = SalesByLocationEntry;

const SalesByLocation = ({
  dateRange,
  locationId,
  tableId,
}: {
  dateRange: { from: Date; to: Date };
  locationId: string;
  tableId: string;
}) => {
  const { data = [], isPending } = useQuery(
    convexQuery(locationSalesGetSalesByLocation, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );
  const isLoading = isPending;

  const columnHelper = createColumnHelper<SalesByLocationData>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("location", {
          header: "Location Name",
          cell: ({ getValue }) => getValue().name,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.location.id),
          meta: {
            faceting: {
              title: "Location",
              getOption: (r: SalesByLocationData["location"]) => ({
                value: r.id,
                label: r.name,
              }),
            },
            getCellForExport: (r: SalesByLocationData["location"]) => r.name,
          },
        }),
        columnHelper.accessor("grossSales", {
          header: "Gross Sales",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: SalesByLocationData["grossSales"]) => d,
          },
        }),
        columnHelper.accessor("netSales", {
          header: "Net Sales",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: SalesByLocationData["netSales"]) => d,
          },
        }),
      ] as ColumnDef<SalesByLocationData>[],
    [columnHelper]
  );

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="text-center">
          <div className="border-foreground mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-b-2" />
          <p className="body-sm text-muted-foreground">
            Loading sales by location...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="subtitle-sm text-foreground">Sales by Location</h3>
      <SalesDataTable columns={columns} data={data} id={tableId} />
    </div>
  );
};

// Sales by Sales Category Component
type SalesBySalesCategoryData = SalesBySalesCategoryEntry;

const SalesBySalesCategory = ({
  dateRange,
  locationId,
  tableId,
}: {
  dateRange: { from: Date; to: Date };
  locationId: string;
  tableId: string;
}) => {
  const { data = [], isPending } = useQuery(
    convexQuery(locationSalesGetSalesBySalesCategory, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );
  const isLoading = isPending;

  const columnHelper = createColumnHelper<SalesBySalesCategoryData>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("salesCategory", {
          header: "Sales Category",
          cell: ({ getValue }) => getValue().name,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.salesCategory.id),
          meta: {
            faceting: {
              title: "Sales Category",
              getOption: (r: SalesBySalesCategoryData["salesCategory"]) => ({
                value: r.id,
                label: r.name,
              }),
            },
            getCellForExport: (r: SalesBySalesCategoryData["salesCategory"]) =>
              r.name,
          },
        }),
        columnHelper.accessor("ordersCount", {
          header: "Orders Count",
          cell: ({ getValue }) => getValue()?.toLocaleString("en-US"),
          meta: {
            getCellForExport: (d: SalesBySalesCategoryData["ordersCount"]) => d,
          },
        }),
        columnHelper.accessor("salesTotal", {
          header: "Sales Total",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: SalesBySalesCategoryData["salesTotal"]) => d,
          },
        }),
      ] as ColumnDef<SalesBySalesCategoryData>[],
    [columnHelper]
  );

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="text-center">
          <div className="border-foreground mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-b-2" />
          <p className="body-sm text-muted-foreground">
            Loading sales by category...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="subtitle-sm text-foreground">Sales by Category</h3>
      <SalesDataTable columns={columns} data={data} id={tableId} />
    </div>
  );
};
